import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "../lib/utils";
import {
  MENU_CHECK,
  MENU_ITEM,
  MENU_LABEL,
  MENU_SEPARATOR,
  OVERLAY_COLLISION_PADDING,
  OVERLAY_FADE,
  OVERLAY_OFFSET,
  OVERLAY_SURFACE,
} from "../lib/overlay";
import { useFieldControl } from "./field";
import { Icon, type IconComponent } from "./icon";
import { INPUT_SIZE, SELF_FRAME, controlState, type InputSize } from "./input";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/**
 * v2 Select (redesign phase 5, #455). Storybook only until implementation;
 * one Select for both apps (Venus's and Neptune's differ by 289 lines: height,
 * check side, purple focus). Same part names as Venus `components/ui/select.tsx`.
 *
 * Trigger = the Input frame (break #90): heights 28 / 32 / 36, `--input`
 * border, hover `--ink-muted`, 2px inset focus ring, red when invalid, 50%
 * when disabled, placeholder in `--muted-foreground`, value in Inter.
 * ChevronDown 16 `--ink-muted`. Joins a Field through `useFieldControl`, so
 * the label names it and hint / error describe it.
 *
 * List: the overlay surface, items as in menus but in Inter (values, not
 * commands); the current value has the Check left in `--primary-accent` and no
 * fill (break #91). Highlight `--hover-overlay`, never purple.
 *
 * For a short, fixed list. With search, many options or several values use
 * Combobox.
 *
 * Disabled: on the Root (`<Select disabled>`) or the trigger; either way the
 * trigger shows the disabled frame (break #176; Radix only tells its own
 * trigger, so `Select` passes the Root's state on through context). With
 * `disabledReason` the trigger works like Button's (finding #21): it stays
 * focusable (`aria-disabled`), the reason is its description and a tooltip,
 * and it never opens. Radix's trigger can't do that (it always sets the native
 * `disabled` from the Root, and its typeahead would still change the value),
 * so a lookalike button takes its place while disabled.
 */

const SelectDisabledContext = React.createContext(false);

function Select({ disabled, ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return (
    <SelectDisabledContext.Provider value={disabled ?? false}>
      <SelectPrimitive.Root disabled={disabled} {...props} />
    </SelectDisabledContext.Provider>
  );
}

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

export interface SelectTriggerProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  size?: InputSize;
  /** With a disabled Root or trigger: why. The trigger stays focusable and says it (description + tooltip). German UI text. */
  disabledReason?: string;
}

/** Shared with Combobox: the Input frame as a button. */
export function triggerClasses({
  size,
  disabled,
  invalid,
}: {
  size: InputSize;
  disabled?: boolean;
  invalid?: React.AriaAttributes["aria-invalid"];
}) {
  return cn(
    "flex w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-md border bg-transparent text-left font-body text-foreground transition-colors",
    INPUT_SIZE[size],
    SELF_FRAME[controlState({ disabled, invalid })],
  );
}

const SelectTrigger = React.forwardRef<React.ElementRef<typeof SelectPrimitive.Trigger>, SelectTriggerProps>(
  function SelectTrigger(rawProps, ref) {
    const { size = "md", className, children, disabledReason, ...props } = useFieldControl(rawProps);
    const rootDisabled = React.useContext(SelectDisabledContext);
    const disabled = props.disabled ?? rootDisabled;
    const reasonId = React.useId();
    const classes = cn(
      triggerClasses({ size, disabled, invalid: props["aria-invalid"] }),
      "data-placeholder:text-muted-foreground",
      className,
    );
    const content = <span className="min-w-0 flex-1 truncate">{children}</span>;
    const chevron = <Icon icon={ChevronDown} />;

    if (disabled && disabledReason) {
      // Radix's handlers stay off: nothing here opens the list or changes the value.
      const { onClick: _onClick, onKeyDown: _onKeyDown, onPointerDown: _onPointerDown, disabled: _disabled, ...rest } = props;
      const describedBy = [props["aria-describedby"], reasonId].filter(Boolean).join(" ");
      return (
        <>
          <Tooltip>
            {/* Not Radix's description: the reason has its own id and is read on focus without the tooltip. */}
            <TooltipTrigger asChild aria-describedby={describedBy}>
              <button
                ref={ref}
                type="button"
                role="combobox"
                aria-expanded={false}
                aria-autocomplete="none"
                {...rest}
                aria-describedby={describedBy}
                aria-disabled="true"
                className={classes}
                onClick={(event) => event.preventDefault()}
              >
                {content}
                <span aria-hidden="true" className="flex shrink-0 text-ink-muted">
                  {chevron}
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent>{disabledReason}</TooltipContent>
          </Tooltip>
          <span id={reasonId} className="sr-only">
            {disabledReason}
          </span>
        </>
      );
    }

    return (
      <SelectPrimitive.Trigger ref={ref} className={classes} {...props}>
        {content}
        <SelectPrimitive.Icon className="flex shrink-0 text-ink-muted">{chevron}</SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
    );
  },
);

const SCROLL_BUTTON = "flex h-6 shrink-0 cursor-default items-center justify-center text-ink-muted";

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(function SelectContent(
  {
    position = "popper",
    sideOffset = OVERLAY_OFFSET,
    collisionPadding = OVERLAY_COLLISION_PADDING,
    className,
    style,
    children,
    ...props
  },
  ref,
) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(OVERLAY_SURFACE, OVERLAY_FADE, "relative flex min-w-40 flex-col overflow-hidden", className)}
        // At least the trigger's width; at most 320 high or the space next to it.
        style={{
          minWidth: "var(--radix-select-trigger-width)",
          maxHeight: "min(var(--radix-select-content-available-height), 20rem)",
          ...style,
        }}
        {...props}
      >
        <SelectPrimitive.ScrollUpButton className={SCROLL_BUTTON}>
          <Icon icon={ChevronUp} />
        </SelectPrimitive.ScrollUpButton>
        <SelectPrimitive.Viewport className="p-1">{children}</SelectPrimitive.Viewport>
        <SelectPrimitive.ScrollDownButton className={SCROLL_BUTTON}>
          <Icon icon={ChevronDown} />
        </SelectPrimitive.ScrollDownButton>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});

export interface SelectItemProps extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> {
  /** 16px before the text; also shown in the trigger with the value. */
  icon?: IconComponent;
}

const SelectItem = React.forwardRef<React.ElementRef<typeof SelectPrimitive.Item>, SelectItemProps>(
  function SelectItem({ icon, className, children, ...props }, ref) {
    return (
      <SelectPrimitive.Item ref={ref} className={cn(MENU_ITEM, "font-body", className)} {...props}>
        <span className={MENU_CHECK}>
          <SelectPrimitive.ItemIndicator className="flex">
            <Icon icon={Check} />
          </SelectPrimitive.ItemIndicator>
        </span>
        <SelectPrimitive.ItemText>
          {icon ? (
            <span className="inline-flex items-center gap-2 align-top">
              <Icon icon={icon} />
              {children}
            </span>
          ) : (
            children
          )}
        </SelectPrimitive.ItemText>
      </SelectPrimitive.Item>
    );
  },
);

/** Group heading, aligned with the item text. */
const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(function SelectLabel({ className, ...props }, ref) {
  return <SelectPrimitive.Label ref={ref} className={cn(MENU_LABEL, "pl-8", className)} {...props} />;
});

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(function SelectSeparator({ className, ...props }, ref) {
  return <SelectPrimitive.Separator ref={ref} className={cn(MENU_SEPARATOR, className)} {...props} />;
});

export { Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectItem, SelectLabel, SelectSeparator };
